import * as React from 'react';
import * as d3Path from 'd3-path';
import { GraphColors, IData, IDataPoint, TGraphScale, TGraphSettings } from 'Components/sq-graphics/interfaces';
import { GraphTooltip, MultipleGraphTooltip } from '../../GraphTooltip';
import { invertColor } from '../../../util/calculators';
import { GraphTooltipDescription } from '../../GraphTooltip/components';
import { sortDate } from 'neuro-utils';
import { equals } from 'ramda';

const createPath = (
  points: IPoint[],
  linegraphProps: IData['linegraphProps'],
  graphHeight: number,
  graphWidth: number,
  xPoint: (date: Date) => number | undefined,
): string => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!points || points.length < 1) return '';
  if (!points[0]) return '';
  const gPath = d3Path.path();
  const xAxisLineBreaks = linegraphProps?.xAxisLineBreaks
    ? linegraphProps.xAxisLineBreaks.map(lb => xPoint(lb))
    : undefined;
  gPath.moveTo(points[0].x, points[0].y);
  const oneDayXLength = (xPoint(new Date()) ?? 0) - (xPoint(new Date(Date.now() - 86400000)) ?? 0);
  points.slice(1).forEach((p, i) => {
    // TODO: The conditions below should be placed/done elsewhere
    const prevPoint = points[i];
    if (prevPoint) {
      // This used with...
      let dontDrawBetweenPoints;
      // ...ResMed graphs to hide path when there is a break bigger than one day (+1 pixel) between points
      const resMedGraphIds = [
        'resMedPap',
        'resMedAhi',
        'aiCentral',
        'resMedMaskLeak95Percentile',
        'resMedPressure95Percentile',
        'tidalVolume95Percentile',
        'respiratoryRate95Percentile',
        'spontT',
        'spontC',
        'targetVentilation',
      ];
      if (resMedGraphIds.includes(p.id) && linegraphProps?.hidePoints && p.x - prevPoint.x > oneDayXLength + 1)
        dontDrawBetweenPoints = true;
      // ...MS MRI graphs to hide path when id matches MRI ids AND preceding pointType is "rect" AND preceding useAdditionalScale is not true
      const msMriIds = ['total', 'infratentorial', 'supratentorial', 'gadoliniumEnhanced'];
      if (
        msMriIds.includes(p.id) &&
        msMriIds.includes(prevPoint.id) &&
        prevPoint.pointType === 'rect' &&
        !prevPoint.useAdditionalScale
      ) {
        dontDrawBetweenPoints = true;
      }
      if (xAxisLineBreaks?.some(lb => lb && lb < p.x && lb > prevPoint.x) || dontDrawBetweenPoints) {
        gPath.moveTo(p.x, p.y);
      } else if (linegraphProps?.stepLine) {
        gPath.lineTo(p.x, prevPoint.y);
        gPath.lineTo(p.x, p.y);
      } else if (linegraphProps?.defaultToZero) {
        gPath.lineTo(prevPoint.x, graphHeight);
        gPath.lineTo(p.x, graphHeight);
        gPath.lineTo(p.x, p.y);
      } else {
        gPath.lineTo(p.x, p.y);
      }
    }
  });
  const lastPoint = points[points.length - 1];
  if (linegraphProps?.stepLine && lastPoint) gPath.lineTo(graphWidth, lastPoint.y);
  return gPath.toString();
};

const getGraphIds = (data: Array<IData>): string[] => {
  const ids: string[] = [];
  data.forEach(d => {
    if (ids.includes(d.id)) return;
    ids.push(d.id);
  });
  return ids;
};

const LineGraph = ({
  data,
  xPoint,
  yPoint,
  height,
  width,
  scale,
  additionalScale,
  graphSettings,
  reference,
  scaleId,
  fullDataIdsList,
  refDataIdsList,
  tabIndex,
}: IOwnProps): React.JSX.Element => {
  // All the IData-objects that are of linegraph type are passed to this component
  const ids: string[] = getGraphIds(data);
  // Ledd graph needs to be reversed as leddMax needs to be rendered under ledd
  if (ids.includes('ledd')) {
    ids.reverse();
  }
  const dataPoints: { [key: string]: IPoint[] } = {};
  ids.forEach(id => {
    const dps: IPoint[] = [];
    data
      .filter(d => d.id === id)
      .forEach(d => {
        let sc: TGraphScale = d.useAdditionalScale && additionalScale ? additionalScale : scale;
        if (reference && additionalScale) sc = additionalScale;
        d.dataPoints.forEach(dp => {
          const x = xPoint(dp.date);
          const y = yPoint(dp.value, sc, height);
          if (!(x || x === 0) || !(y || y === 0)) return;
          dps.push({
            id: id,
            x: x,
            y: y,
            value: dp.value,
            date: dp.date,
            legend: d.legend,
            title: dp.title,
            description: dp.description,
            alternativeDateString: dp.alternativeDateString,
            useAdditionalScale: d.useAdditionalScale,
            pointType: dp.pointType,
            hideValue: dp.hideValue,
            legendModifier: dp.legendModifier,
          });
        });
      });
    dataPoints[id] = dps;
  });
  const multipleDataPoints: IMultiplePoint[] = [];
  const allDataPoints: IPoint[] = [];
  Object.values(dataPoints).forEach(dps => {
    allDataPoints.push(...dps);
  });

  const sortedAllDataPoints = allDataPoints.toSorted((dp1, dp2) => sortDate(dp1.date, dp2.date));

  const step = 12;

  // Hack to make seizures per day graph not merge datapoints
  if (
    !data.find(d => d.linegraphProps?.defaultToZero || d.linegraphProps?.doNotMergeOverlappingDataPoints) &&
    sortedAllDataPoints.length > 1
  ) {
    // Get name based on legend and legendModifier
    const getName = (data: IData | undefined, point: IPoint): string | undefined =>
      point.legendModifier
        ? typeof point.legendModifier === 'string'
          ? point.legendModifier
          : data?.legend
            ? `${point.legendModifier.prefix ?? ''}${data.legend}${point.legendModifier.suffix ?? ''}`
            : undefined
        : data?.legend;
    // Iterate through all the dataPoints
    for (let i = 0; i < sortedAllDataPoints.length - 1; i++) {
      let dataPointsInsideSquare = 1;
      const tooltipContents: Array<{
        title: string;
        date?: Date;
        start?: Date;
        end?: Date;
        description: any;
        name?: string;
      }> = [];
      // Assign the next dataPoint's index into a variable named ix
      let ix = i + 1;
      // Find yStart and xStart values for the current dataPoint
      const yStart = Math.floor(sortedAllDataPoints[i].y / step) * step;
      const xStart = Math.floor(sortedAllDataPoints[i].x / step) * step;
      // Here we check all the dataPoints within xStart and xStart+step. If overlapping dataPoints are found, handle them here
      while (
        ix <= sortedAllDataPoints.length - 1 &&
        sortedAllDataPoints[ix].x >= xStart &&
        sortedAllDataPoints[ix].x < xStart + step
      ) {
        // If datapoint already has been taken into action, skip.
        if (sortedAllDataPoints[ix].doNotDraw) {
          ix += 1;
          continue;
        }
        // If dataPoint with x value within range is found, mark it as doNotDraw
        if (sortedAllDataPoints[ix].y >= yStart && sortedAllDataPoints[ix].y < yStart + step) {
          sortedAllDataPoints[ix].doNotDraw = true;
          dataPointsInsideSquare += 1;

          const thisData = data.find(d => d.id === sortedAllDataPoints[ix].id);
          const thisDp = sortedAllDataPoints[ix];
          const name = getName(thisData, thisDp);

          tooltipContents.push({
            title: sortedAllDataPoints[ix]?.title ?? '',
            date: sortedAllDataPoints[ix]?.date,
            description: sortedAllDataPoints[ix]?.description,
            name: name,
          });
        }
        ix += 1;
      }
      // Here make the multipleDataPoint
      if (dataPointsInsideSquare > 1) {
        const thisData = data.find(d => d.id === sortedAllDataPoints[i].id);
        const thisDp = sortedAllDataPoints[i];
        const name = getName(thisData, thisDp);

        // If overlapping datapoints are found, push the current dataPoint's information to the tooltip content array
        tooltipContents.push({
          title: sortedAllDataPoints[i]?.title ?? '',
          date: sortedAllDataPoints[i]?.date,
          description: sortedAllDataPoints[i]?.description,
          name: name,
        });
        sortedAllDataPoints[i].doNotDraw = true;
        multipleDataPoints.push({
          id: sortedAllDataPoints[i].id,
          n: dataPointsInsideSquare,
          x: xStart + step / 2,
          y: thisDp.value === 0 ? yStart : yStart + step / 2,
          tooltipContents: tooltipContents,
        });
      }
    }
  }

  const mergedDataPoints: { [key: string]: IPoint[] } = {};

  ids.forEach(id => {
    mergedDataPoints[id] = sortedAllDataPoints.filter(dp => dp.id === id);
  });

  const [showLine, setShowLine] = React.useState<number | null>(null);
  const [showLineId, setShowLineId] = React.useState<string | null>(null);

  const sameColorSets =
    scaleId === 'tdcsTreatmentsPerWeek' && fullDataIdsList.every(item => typeof item === 'string')
      ? [
          ...new Set(
            (reference && refDataIdsList ? refDataIdsList : fullDataIdsList).map(
              mdpKey => mdpKey.split('-')[0] + mdpKey.split('-')[1],
            ),
          ),
        ]
      : undefined;

  return (
    <g>
      {Object.entries(mergedDataPoints).map(([key, points]) => {
        let color: string;
        if (scaleId === 'tdcsTreatmentsPerWeek') {
          const usedKeyForColor = key.split('-')[0] + key.split('-')[1];
          let keyColorIndex = sameColorSets?.findIndex(s => s === usedKeyForColor);
          if (keyColorIndex === undefined || keyColorIndex === -1) keyColorIndex = 0;
          color = Object.values(GraphColors)[keyColorIndex % Object.values(GraphColors).length];
        } else {
          const colorIndex = (
            reference && refDataIdsList
              ? refDataIdsList
              : !fullDataIdsList.every(item => typeof item === 'string')
                ? fullDataIdsList[tabIndex ?? 0]
                : fullDataIdsList
          ).findIndex(dataId => dataId === key);
          color = graphSettings?.colors?.[key]
            ? GraphColors[graphSettings.colors[key]]
            : Object.values(GraphColors)[colorIndex % Object.values(GraphColors).length];
        }

        if (reference) color = invertColor(color);
        const linegraphProps = data.find(d => d.id === key)?.linegraphProps;
        const path: { path: string; color: string; strokeWidth: number } = {
          path: createPath(points, linegraphProps, height, width, xPoint),
          color: color,
          strokeWidth: linegraphProps?.stepLine ? 1.5 : 2.5,
        };
        return (
          <g key={key}>
            <g>
              {path.path && (
                <path d={path.path} stroke={path.color} strokeWidth={path.strokeWidth + 'px'} fill="none" opacity={1} />
              )}
            </g>

            {points
              .filter(p => !p.doNotDraw)
              .map((p, i) => {
                return (
                  <g key={`${key}${i}`}>
                    {((additionalScale && !additionalScale.dontDrawHelperLine) || reference) && (
                      <line
                        x1={p.x}
                        x2={p.useAdditionalScale || reference ? '100%' : 0}
                        y1={p.y}
                        y2={p.y}
                        stroke={'black'}
                        strokeWidth="0.5"
                        strokeDasharray={5}
                        opacity={showLine === i && showLineId === key ? 1 : 0}
                      />
                    )}
                    <GraphTooltip
                      date={p.date}
                      name={
                        p.legendModifier
                          ? typeof p.legendModifier === 'string'
                            ? p.legendModifier
                            : p.legend
                              ? `${p.legendModifier.prefix ?? ''}${p.legend}${p.legendModifier.suffix ?? ''}`
                              : undefined
                          : p.legend
                      }
                      title={p.title ?? p.value.toString()}
                      description={
                        p.description ? <GraphTooltipDescription convertedData={p.description} /> : undefined
                      }
                      content={
                        p.pointType === 'triangle' ? (
                          <svg
                            x={p.x - (linegraphProps?.defaultToZero ? 4 : 8)}
                            y={p.y - (linegraphProps?.defaultToZero ? 4 : 8)}
                            viewBox="0 0 25 25"
                            width={linegraphProps?.hidePoints ? '2.5' : linegraphProps?.defaultToZero ? 8 : 16}
                            height={linegraphProps?.hidePoints ? '2.5' : linegraphProps?.defaultToZero ? 8 : 16}
                            fill={linegraphProps?.hidePoints ? 'transparent' : color}
                          >
                            <path d="M24 22h-24l12-20z" />
                          </svg>
                        ) : p.pointType === 'rect' ? (
                          <rect
                            key={`${p.x}${p.y}`}
                            x={p.x - (linegraphProps?.defaultToZero ? 3 : 6)}
                            y={p.y - (linegraphProps?.defaultToZero ? 3 : 6)}
                            width={linegraphProps?.hidePoints ? '2.5' : linegraphProps?.defaultToZero ? 6 : 12}
                            height={linegraphProps?.hidePoints ? '2.5' : linegraphProps?.defaultToZero ? 6 : 12}
                            fill={linegraphProps?.hidePoints ? 'transparent' : color}
                            onMouseEnter={
                              additionalScale && !additionalScale.dontDrawHelperLine
                                ? () => {
                                    setShowLine(i);
                                    setShowLineId(key);
                                  }
                                : () => null
                            }
                            onMouseLeave={() => {
                              setShowLine(null);
                              setShowLineId(null);
                            }}
                          />
                        ) : (
                          <circle
                            key={`${p.id}`}
                            cx={p.x}
                            cy={p.y}
                            r={
                              linegraphProps?.hidePoints && points.length > 1
                                ? '2.5'
                                : linegraphProps?.defaultToZero
                                  ? '3'
                                  : '6'
                            }
                            fill={linegraphProps?.hidePoints && points.length > 1 ? 'transparent' : color}
                            onMouseEnter={
                              additionalScale && !additionalScale.dontDrawHelperLine
                                ? () => {
                                    setShowLine(i);
                                    setShowLineId(key);
                                  }
                                : () => null
                            }
                            onMouseLeave={() => {
                              setShowLine(null);
                              setShowLineId(null);
                            }}
                          />
                        )
                      }
                      alternativeDateString={p.alternativeDateString}
                      hideValue={p.hideValue}
                    />
                  </g>
                );
              })}
          </g>
        );
      })}
      {multipleDataPoints.map((p, i) => {
        const linegraphProps = data.find(d => d.id === p.id)?.linegraphProps;
        return (
          <MultipleGraphTooltip
            key={`${i}${p.n}${p.x}${p.y}`}
            data={p.tooltipContents}
            content={
              <g>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={linegraphProps?.hidePoints ? '2.5' : '10'}
                  fill={linegraphProps?.hidePoints ? 'transparent' : 'white'}
                  stroke={linegraphProps?.hidePoints ? 'transparent' : '#8D8D8D'}
                />
                <text
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontWeight="bold"
                  fontSize="12"
                  fill={linegraphProps?.hidePoints ? 'transparent' : '#8D8D8D'}
                  y={p.y}
                  x={p.x}
                >
                  {p.n > 9 ? '9+' : `${p.n}`}
                </text>
              </g>
            }
            alternativeDateString={p.alternativeDateString}
          />
        );
      })}
    </g>
  );
};

interface IMultiplePoint {
  id: string;
  n: number;
  x: number;
  y: number;
  tooltipContents: Array<{ title: string; date?: Date; start?: Date; end?: Date; description: any; name?: string }>;
  alternativeDateString?: string;
}

interface IPoint {
  id: string;
  x: number;
  y: number;
  value: string | number;
  date: Date;
  legend?: string;
  title?: string;
  description?: IDataPoint['description'];
  doNotDraw?: boolean;
  alternativeDateString?: string;
  useAdditionalScale?: boolean;
  pointType?: 'rect' | 'circle' | 'triangle';
  hideValue?: IDataPoint['hideValue'];
  legendModifier?: IDataPoint['legendModifier'];
}

interface IOwnProps {
  data: IData[];
  xPoint: (date: Date) => number | undefined;
  yPoint: (value: string | number, scale: TGraphScale, graphHeight: number) => number | undefined;
  height: number;
  width: number;
  scale: TGraphScale;
  additionalScale: TGraphScale | undefined;
  graphSettings: TGraphSettings['graphSettings'];
  reference?: true;
  scaleId?: string; // Used to color ninmt tdcsTreatmentsPerWeek graph dataPoints
  fullDataIdsList: string[] | Array<Array<string>>;
  refDataIdsList?: string[];
  tabIndex?: number;
  timeframe?: [Date, Date];
}

// Memoize the LineGraph component to prevent unnecessary re-renders
export default React.memo(LineGraph, (prevProps, nextProps) => {
  if (!equals(prevProps.timeframe, nextProps.timeframe)) {
    return false;
  }

  if (prevProps.data.length !== nextProps.data.length) {
    return false;
  }

  if (
    prevProps.height !== nextProps.height ||
    prevProps.width !== nextProps.width ||
    prevProps.reference !== nextProps.reference ||
    prevProps.scaleId !== nextProps.scaleId ||
    prevProps.tabIndex !== nextProps.tabIndex
  ) {
    return false;
  }

  for (let i = 0; i < prevProps.data.length; i++) {
    const prev = prevProps.data[i];
    const next = nextProps.data[i];

    if (prev?.id !== next?.id || prev?.legend !== next?.legend || prev?.dataPoints.length !== next?.dataPoints.length) {
      return false;
    }

    // Check if dataPoints have changed (compare a few key data points)
    const pointsToCheck = Math.min(3, prev?.dataPoints.length);
    for (let j = 0; j < pointsToCheck; j++) {
      const prevPoint = prev.dataPoints[j];
      const nextPoint = next.dataPoints[j];
      if (prevPoint?.date.getTime() !== nextPoint?.date.getTime() || prevPoint?.value !== nextPoint?.value) {
        return false;
      }
    }
  }

  if (
    prevProps.scale.id !== nextProps.scale.id ||
    prevProps.scale.type !== nextProps.scale.type ||
    !equals(prevProps.scale.linearScale, nextProps.scale.linearScale) ||
    !equals(prevProps.scale.customScale, nextProps.scale.customScale)
  ) {
    return false;
  }

  if (!equals(prevProps.additionalScale, nextProps.additionalScale)) {
    return false;
  }

  if (!equals(prevProps.fullDataIdsList, nextProps.fullDataIdsList)) {
    return false;
  }

  return true;
});
